import { useState, useRef } from "react";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `/api`;

export function ImageUpload({ value, onChange, label = "Image" }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || "");
  const [urlMode, setUrlMode] = useState(!value || value.startsWith("http"));
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please select a valid image file (JPG, PNG, GIF, WEBP)");
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File is too large. Maximum size is 10MB");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      const imageUrl = response.data.url;
      setPreview(imageUrl);
      onChange(imageUrl);
      toast.success("Image uploaded successfully!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setPreview(url);
    onChange(url);
  };

  const clearImage = () => {
    setPreview("");
    onChange("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-[#94A3B8]">{label}</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={urlMode ? "default" : "outline"}
            size="sm"
            onClick={() => setUrlMode(true)}
            className={urlMode ? "bg-[#00AEEF] hover:bg-[#0095CC] h-7 text-xs" : "border-[#2B4A6F] h-7 text-xs"}
          >
            URL
          </Button>
          <Button
            type="button"
            variant={!urlMode ? "default" : "outline"}
            size="sm"
            onClick={() => setUrlMode(false)}
            className={!urlMode ? "bg-[#00AEEF] hover:bg-[#0095CC] h-7 text-xs" : "border-[#2B4A6F] h-7 text-xs"}
          >
            Upload
          </Button>
        </div>
      </div>

      {urlMode ? (
        <Input
          value={preview}
          onChange={handleUrlChange}
          placeholder="https://example.com/image.jpg"
          className="bg-[#152A47] border-[#2B4A6F] text-white"
          data-testid="image-url-input"
        />
      ) : (
        <div className="space-y-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="image-file-input"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full h-20 border-dashed border-2 border-[#2B4A6F] hover:border-[#00AEEF] bg-[#152A47]/50"
            data-testid="image-upload-btn"
          >
            {uploading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Uploading...</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="w-6 h-6 text-[#64748B]" />
                <span className="text-[#64748B] text-sm">Click to upload image</span>
                <span className="text-[#475569] text-xs">JPG, PNG, GIF, WEBP (max 10MB)</span>
              </div>
            )}
          </Button>
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div className="relative mt-3">
          <div className="relative rounded-lg overflow-hidden border border-[#2B4A6F] bg-[#152A47]">
            <img
              src={preview}
              alt="Preview"
              className="w-full h-40 object-cover"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
            <div className="hidden w-full h-40 items-center justify-center bg-[#152A47]">
              <div className="text-center text-[#64748B]">
                <ImageIcon className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">Failed to load image</p>
              </div>
            </div>
          </div>
          <Button
            type="button"
            variant="destructive"
            size="icon"
            onClick={clearImage}
            className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
            data-testid="clear-image-btn"
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default ImageUpload;
